import './styles/theme.css';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { initOfflineSync } from './lib/offline-queue';
import { routeTree } from './routeTree.gen';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  context: { queryClient },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('No #root element');

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);

// Register the PWA service worker (offline shell + lesson caching + push).
// Dev (localhost:5175) registers too so the install/offline flow is testable.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => {
      // Registration failures are non-fatal — the app works without the SW.
    });
  });
}

// Replay any lesson writes that were queued offline, on reconnect + at startup.
// After a successful sync, refetch the learn + progress views so the real
// (server-scored) result replaces the optimistic one.
initOfflineSync(() => {
  void queryClient.invalidateQueries({ queryKey: ['kids'] });
  void queryClient.invalidateQueries({ queryKey: ['parent', 'progress'] });
});
